import { v4 as uuidv4 } from 'uuid';

import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';

import { experimentEligibilityReceived } from './experimentEligibilitySlice';
import { variantsReceived } from './variantsSlice';

import type { RemoteEvaluationClient } from '@amplitude/experiment-node-server';
import type { Request, Response } from 'express';
import type { Cookies } from 'react-cookie';
import type { Dispatch } from 'redux';

const logger = Logger.getLogger('wanderu-analytics');

let experiment: RemoteEvaluationClient;

type IneligibilityReason = string | null;
interface IneligibilityProperties {
    accept?: string;
    acceptLanguage?: string;
    cfBotScore?: number;
    userAgent?: string;
}
type EligibilityReturnValue = [boolean, IneligibilityReason, IneligibilityProperties];

function getIsEligibleForAssignment(req: Request): EligibilityReturnValue {
    const userAgent = req?.headers?.['user-agent'] || '';

    // Check Cloudflare headers
    const cfBotScore = Number(req?.headers?.['cf-bot-score']);
    if (cfBotScore && cfBotScore < 100) return [false, 'Cloudflare bot score', { cfBotScore }];

    // Check for missing/suspicious headers
    if (!userAgent || !req?.headers?.['accept-language']) return [false, 'missing/suspicious headers', { acceptLanguage: req?.headers?.['accept-language'], userAgent }];

    // Missing critical browser headers
    const hasAccept = req?.headers?.accept;
    if (!hasAccept || !hasAccept.includes('text/html')) return [false, 'missing critical browser headers', { accept: req?.headers?.accept }];

    return [true, null, {}];
}

interface FetchServerVariantsParams {
    cookies: Cookies;
    dispatch: Dispatch;
    req: Request;
    res: Response;
}

export async function fetchServerVariants({
    cookies,
    dispatch,
    req,
    res,
}: FetchServerVariantsParams) {
    const apiKey = appSettings?.config?.amplitude?.apiKey;
    const isServerSide = typeof window === 'undefined';

    if (!apiKey || !isServerSide) {
        return;
    }

    const deviceId = await handleAmplitudeCookies(cookies, res);

    const [
        isEligibleForAssignment,
        ineligibilityReason,
        ineligibilityProperties,
    ] = getIsEligibleForAssignment(req);

    logger.event('isEligibleForAssignment', {
        deviceId,
        ineligibilityReason,
        isEligibleForAssignment,
        ...ineligibilityProperties,
    });

    const experimentEligibility = {
        ineligibilityProperties,
        ineligibilityReason,
        isEligibleForAssignment,
    };

    dispatch(experimentEligibilityReceived({ experimentEligibility }));

    if (!isEligibleForAssignment) {
        return;
    }

    if (!experiment) {
        const config = {
            fetchRetries: 1,
            fetchRetryBackoffMinMillis: 0,
            fetchRetryTimeoutMillis: 500,
            fetchTimeoutMillis: 500,
        };

        // eslint-disable-next-line import/no-extraneous-dependencies, global-require, @typescript-eslint/no-require-imports
        experiment = require('@amplitude/experiment-node-server')
            .Experiment
            .initializeRemote(apiKey, config);
    }

    const variants = await experiment
        .fetchV2({ device_id: deviceId });

    dispatch(variantsReceived({ variants }));
}

async function handleAmplitudeCookies(cookies: Cookies, res: Response) { // https://amplitude.com/docs/sdks/experiment-sdks/experiment-node-js#access-amplitude-cookies
    let deviceId: string | undefined;

    const apiKey = appSettings?.config?.amplitude?.apiKey;

    // eslint-disable-next-line import/no-extraneous-dependencies, global-require, @typescript-eslint/no-require-imports
    const { AmplitudeCookie } = require('@amplitude/experiment-node-server');

    // Get the cookie name for the Amplitude API key
    const ampCookieName = AmplitudeCookie.cookieName(apiKey, true);

    const cookie = cookies.get(ampCookieName);
    // Try to get device ID from existing cookie
    if (cookie) {
        deviceId = AmplitudeCookie.parse(cookie, true).device_id;
    }

    // If no device ID found, generate a new one and set the cookie
    if (!deviceId) {
        deviceId = uuidv4();

        const ampCookieValue = AmplitudeCookie.generate(deviceId, true);

        cookies.set(ampCookieName, ampCookieValue);
        res.cookie(ampCookieName, ampCookieValue, {
            domain: '.wanderu.com', // this should be the same domain used by the Amplitude JS SDK
            httpOnly: false,
            secure: false,
        });
    }

    return deviceId;
}
